"use client";

import React, { forwardRef } from "react";
import { Icon as IconButton } from "@churchofjesuschrist/eden-buttons";

/**
 * A generic action button for use in the `ActionBar`.
 * @param {object} props - The component props
 * @param {string} [props.label] - Hidden text label.
 * @param {string} [props.href] - If present, the root element is an `<a>`, otherwise it's a `<button>`.
 * @param {boolean} [props.active] - When present, the action becomes a "toggle" button.
 * @param {boolean} [props.disabled] - Whether to disable the button.
 * @param {React.ReactNode} props.Icon - Icon component to display in the button.
 * @param {boolean} [props."aria-pressed"] - When present, the action becomes a "toggle" button. The value indicates whether it is toggled on or not. To indicate that a toggle action is off use aria-pressed={false} instead of excluding the prop.
 * @param {React.Ref} ref - Ref forwarded to the underlying button or anchor element.
 * @returns {React.ReactElement} The Action component
 */
import { jsx as _jsx } from "react/jsx-runtime";
const ActionComponent = ({
  label = "Action",
  href,
  "aria-pressed": ariaPressed,
  disabled,
  Icon,
  ...props
}, ref) => /*#__PURE__*/_jsx(IconButton, {
  ref: ref,
  href: href,
  disabled: disabled,
  "aria-pressed": ariaPressed,
  "aria-label": label,
  ...props,
  children: /*#__PURE__*/_jsx(Icon, {
    size: "1.5rem"
  })
});
const Action = /*#__PURE__*/forwardRef(ActionComponent);
Action.displayName = "Action";
export default Action;